import React, { useState, useEffect } from 'react';
import {
  CogIcon,
  CloudIcon,
  EnvelopeIcon,
  ServerIcon,
  ChevronDownIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  LinkIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { configAPI } from '../services/api';

const ConfigSelector = ({ 
  category, 
  selectedConfig, 
  onConfigSelect, 
  label, 
  description,
  allowCustom = true,
  required = false 
}) => {
  const [options, setOptions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [customValue, setCustomValue] = useState('');
  const [useCustom, setUseCustom] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOptions();
  }, [category]);

  const fetchOptions = async () => {
    try {
      const response = await configAPI.getAll();
      const categoryConfigs = response.data[category] || {};
      
      const configOptions = Object.keys(categoryConfigs).map(key => ({
        key,
        label: categoryConfigs[key].description || key,
        value: categoryConfigs[key].value,
        encrypted: categoryConfigs[key].encrypted,
        hasValue: !!categoryConfigs[key].value && categoryConfigs[key].value !== '********'
      }));

      setOptions(configOptions);
    } catch (error) {
      console.error('Erro ao buscar configurações:', error);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryIcon = () => {
    switch (category) {
      case 'aws':
        return <CloudIcon className="h-4 w-4" />;
      case 'email':
        return <EnvelopeIcon className="h-4 w-4" />;
      case 'database':
        return <ServerIcon className="h-4 w-4" />;
      default:
        return <CogIcon className="h-4 w-4" />;
    }
  };

  const getCategoryColor = () => {
    switch (category) {
      case 'aws':
        return 'text-orange-600 bg-orange-100';
      case 'email':
        return 'text-blue-600 bg-blue-100';
      case 'database':
        return 'text-green-600 bg-green-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const handleOptionSelect = (option) => {
    setUseCustom(false);
    setIsOpen(false);
    onConfigSelect({
      type: 'global',
      key: option.key,
      value: option.value,
      source: 'global_config'
    });
  };

  const handleCustomSelect = () => {
    setUseCustom(true);
    setIsOpen(false);
    onConfigSelect({
      type: 'custom',
      value: customValue,
      source: 'manual'
    });
  };

  const handleCustomValueChange = (value) => {
    setCustomValue(value);
    if (useCustom) {
      onConfigSelect({
        type: 'custom',
        value: value,
        source: 'manual'
      });
    }
  };

  const getCurrentDisplay = () => {
    if (selectedConfig?.type === 'global') {
      const option = options.find(opt => opt.key === selectedConfig.key);
      return option ? option.label : selectedConfig.key;
    } else if (selectedConfig?.type === 'custom') {
      return customValue || 'Valor personalizado';
    }
    return 'Selecionar configuração...';
  };

  const hasValidOptions = options.filter(opt => opt.hasValue).length > 0;

  if (loading) {
    return (
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          {label}
        </label>
        <div className="animate-pulse bg-gray-200 h-10 rounded-md"></div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
        <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor()}`}>
          {getCategoryIcon()}
          <span className="ml-1 capitalize">{category}</span>
        </div>
      </div>
      
      {description && (
        <p className="text-sm text-gray-600">{description}</p>
      )}

      <div className="relative">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="relative w-full bg-white border border-gray-300 rounded-md pl-3 pr-10 py-2 text-left cursor-default focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
        >
          <span className="block truncate">{getCurrentDisplay()}</span>
          <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
            <ChevronDownIcon className="h-5 w-5 text-gray-400" />
          </span>
        </button>

        {isOpen && (
          <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
            
            {/* Configurações globais disponíveis */}
            {hasValidOptions && (
              <>
                <div className="px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wide bg-gray-50">
                  Configurações Globais
                </div>
                {options
                  .filter(option => option.hasValue)
                  .map((option) => (
                    <div
                      key={option.key}
                      onClick={() => handleOptionSelect(option)}
                      className="cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-gray-50"
                    >
                      <div className="flex items-center">
                        <span className="block truncate font-medium">
                          {option.label}
                        </span>
                        {option.encrypted && (
                          <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                            Criptografado
                          </span>
                        )}
                      </div>
                      <div className="flex items-center mt-1">
                        <LinkIcon className="h-3 w-3 text-gray-400 mr-1" />
                        <span className="text-xs text-gray-500">global_config.{option.key}</span>
                      </div>
                      {selectedConfig?.type === 'global' && selectedConfig.key === option.key && (
                        <span className="absolute inset-y-0 right-0 flex items-center pr-4">
                          <CheckCircleIcon className="h-5 w-5 text-blue-600" />
                        </span>
                      )}
                    </div>
                  ))
                }
              </>
            )}

            {/* Configurações globais sem valor */}
            {options.filter(opt => !opt.hasValue).length > 0 && (
              <>
                <div className="px-3 py-2 text-xs font-medium text-gray-400 uppercase tracking-wide bg-gray-50 border-t">
                  Configurações sem valor
                </div>
                {options
                  .filter(option => !option.hasValue)
                  .map((option) => (
                    <div
                      key={option.key}
                      className="relative py-2 pl-3 pr-9 text-gray-400 cursor-not-allowed"
                    >
                      <div className="flex items-center">
                        <ExclamationTriangleIcon className="h-4 w-4 mr-2" />
                        <span className="block truncate">
                          {option.label}
                        </span>
                      </div>
                      <div className="flex items-center mt-1">
                        <span className="text-xs">global_config.{option.key} (sem valor)</span>
                      </div>
                    </div>
                  ))
                }
              </>
            )}

            {/* Opção personalizada */}
            {allowCustom && (
              <>
                <div className="px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wide bg-gray-50 border-t">
                  Valor personalizado
                </div>
                <div
                  onClick={() => handleCustomSelect()}
                  className="cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-gray-50"
                >
                  <div className="flex items-center">
                    <span className="block truncate font-medium">
                      Inserir valor manual
                    </span>
                  </div>
                  <span className="text-xs text-gray-500">Valor específico para esta configuração</span>
                  {selectedConfig?.type === 'custom' && (
                    <span className="absolute inset-y-0 right-0 flex items-center pr-4">
                      <CheckCircleIcon className="h-5 w-5 text-blue-600" />
                    </span>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Campo de valor personalizado */}
      {useCustom && (
        <div className="mt-2">
          <input
            type="text"
            value={customValue}
            onChange={(e) => handleCustomValueChange(e.target.value)}
            placeholder="Digite o valor personalizado..."
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      )}

      {/* Status da configuração */}
      {selectedConfig && (
        <div className="flex items-center text-sm">
          {selectedConfig.type === 'global' ? (
            <div className="flex items-center text-green-600">
              <LinkIcon className="h-4 w-4 mr-1" />
              <span>Usando configuração global: {selectedConfig.key}</span>
            </div>
          ) : (
            <div className="flex items-center text-blue-600">
              <CogIcon className="h-4 w-4 mr-1" />
              <span>Usando valor personalizado</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ConfigSelector;